<template>
    <div ref="trigger" class="tw:inline-block tw:text-left">
        <span class="tw:inline-block">
            <slot name="trigger" :open="open" :toggle="toggle">
                <BaseButton type="button" :variant="variant" :size="size" :aria-expanded="open ? 'true' : 'false'"
                    aria-haspopup="menu" @click="toggle">
                    <i v-if="icon" :class="icon"></i>
                    {{ label }}
                    <i class="fa fa-chevron-down tw:text-[0.6rem] tw:opacity-70 tw:transition-transform tw:duration-200"
                        :class="{ 'tw:rotate-180': open }"></i>
                </BaseButton>
            </slot>
        </span>

        <!-- El menú se mueve a <body> en mounted() para que ningún contenedor con overflow lo recorte -->
        <div ref="menu" v-show="open" role="menu" :style="menuStyle"
            class="tw:bg-white tw:rounded-xl tw:shadow-xl tw:border tw:border-slate-200/70 tw:p-1.5 tw:overflow-y-auto tw:transition tw:duration-150 tw:ease-out"
            :class="[
                width,
                positioned ? 'tw:opacity-100 tw:scale-100' : 'tw:opacity-0 tw:scale-95',
                placement === 'top' ? 'tw:origin-bottom-right' : 'tw:origin-top-right'
            ]">
            <slot :close="close"></slot>
        </div>
    </div>
</template>

<script>
const GAP = 6;
const VIEWPORT_MARGIN = 8;

export default {
    name: 'BaseDropdown',
    provide() {
        return { baseDropdown: this };
    },
    props: {
        label: { type: String, default: 'Acciones' },
        icon: { type: String, default: 'fa fa-list' },
        variant: { type: String, default: 'secondary' },
        size: { type: String, default: 'sm' },
        // Clase de ancho del menú (Tailwind)
        width: { type: String, default: 'tw:w-56' },
        // Alineación horizontal respecto al botón: 'right' o 'left'
        align: { type: String, default: 'right', validator: (v) => ['right', 'left'].includes(v) },
    },
    data() {
        return {
            open: false,
            positioned: false,
            placement: 'bottom',
            coords: { top: 0, left: 0, maxHeight: null },
        };
    },
    computed: {
        menuStyle() {
            return {
                position: 'fixed',
                zIndex: 9999,
                top: `${this.coords.top}px`,
                left: `${this.coords.left}px`,
                maxHeight: this.coords.maxHeight !== null ? `${this.coords.maxHeight}px` : null,
                // Oculto hasta tener posición calculada: evita que aparezca en un lugar y luego salte
                visibility: this.positioned ? 'visible' : 'hidden',
            };
        },
    },
    mounted() {
        document.body.appendChild(this.$refs.menu);
    },
    deactivated() {
        this.close();
    },
    beforeDestroy() {
        this.removeListeners();
        const menu = this.$refs.menu;
        if (menu && menu.parentNode) menu.parentNode.removeChild(menu);
    },
    methods: {
        toggle() {
            this.open ? this.close() : this.show();
        },
        show() {
            if (this.open) return;
            this.positioned = false;
            this.coords.maxHeight = null;
            this.open = true;
            this.addListeners();
            this.$nextTick(() => {
                if (!this.open) return;
                this.updatePosition();
                if (!this.open) return;
                this.positioned = true;
                this.$emit('open');
            });
        },
        close() {
            if (!this.open) return;
            this.open = false;
            this.positioned = false;
            this.removeListeners();
            this.$emit('close');
        },
        updatePosition() {
            const trigger = this.$refs.trigger;
            const menu = this.$refs.menu;
            if (!trigger || !menu) return;

            const rect = trigger.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const menuWidth = menu.offsetWidth;
            const menuHeight = menu.scrollHeight;

            // Si el botón salió de la pantalla (scroll), se cierra el menú
            if (rect.bottom < 0 || rect.top > vh) {
                this.close();
                return;
            }

            let left = this.align === 'right' ? rect.right - menuWidth : rect.left;
            left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - menuWidth - VIEWPORT_MARGIN));

            const spaceBelow = vh - rect.bottom - GAP - VIEWPORT_MARGIN;
            const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
            const fitsBelow = menuHeight <= spaceBelow;
            const openUp = !fitsBelow && spaceAbove > spaceBelow;

            let top;
            let maxHeight = null;
            if (openUp) {
                this.placement = 'top';
                if (menuHeight > spaceAbove) maxHeight = Math.max(0, spaceAbove);
                top = rect.top - GAP - Math.min(menuHeight, spaceAbove);
            } else {
                this.placement = 'bottom';
                if (!fitsBelow) maxHeight = Math.max(0, spaceBelow);
                top = rect.bottom + GAP;
            }

            this.coords = { top, left, maxHeight };
        },
        onScrollOrResize() {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                if (this.open) this.updatePosition();
            });
        },
        onDocumentClick(event) {
            const { trigger, menu } = this.$refs;
            if (trigger.contains(event.target) || menu.contains(event.target)) return;
            this.close();
        },
        onKeydown(event) {
            if (event.key === 'Escape') this.close();
        },
        addListeners() {
            document.addEventListener('click', this.onDocumentClick, true);
            document.addEventListener('keydown', this.onKeydown);
            window.addEventListener('scroll', this.onScrollOrResize, true);
            window.addEventListener('resize', this.onScrollOrResize);
        },
        removeListeners() {
            document.removeEventListener('click', this.onDocumentClick, true);
            document.removeEventListener('keydown', this.onKeydown);
            window.removeEventListener('scroll', this.onScrollOrResize, true);
            window.removeEventListener('resize', this.onScrollOrResize);
            if (this._raf) {
                cancelAnimationFrame(this._raf);
                this._raf = null;
            }
        },
    },
};
</script>
